class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
/* ============================================================
   EDITAL ACADEMY — "YOU MIGHT ALSO LIKE" ADD BUTTON
   Namespaced custom element, only defined once, uses the same
   Shopify AJAX Cart API + Section Rendering flow as the native
   product-form.js so it stays in sync with everything else
   (progress bar, promo card, prices, cart-icon count).
   ============================================================ */

if (!customElements.get('cart-drawer-recommendation')) {
  class CartDrawerRecommendation extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.edital-rec-add');
      this.errorEl = this.querySelector('.edital-rec-error');
      this.variantId = this.dataset.variantId;
      this.button.addEventListener('click', this.onAddClick.bind(this));
    }

    onAddClick(event) {
      event.preventDefault();

      // Prevent double-click duplicate additions
      if (this.button.getAttribute('aria-busy') === 'true') return;

      if (!this.variantId || this.variantId === '') {
        this.showError(window.cartStrings ? window.cartStrings.error : 'This product is unavailable.');
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      this.setLoading(true);
      this.hideError();

      const sections = cartDrawer
        ? cartDrawer.getSectionsToRender().map((section) => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      const body = JSON.stringify({
        id: this.variantId,
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname,
      });

      fetch(window.routes.cart_add_url, { ...fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || (window.cartStrings ? window.cartStrings.error : 'Could not add this item.'));
            return;
          }

          if (cartDrawer) {
            cartDrawer.renderContents(response);
          }
        })
        .catch(() => {
          this.showError(window.cartStrings ? window.cartStrings.error : 'Could not add this item.');
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    setLoading(isLoading) {
      this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.button.disabled = isLoading;
      const spinner = this.button.querySelector('.edital-rec-add-spinner');
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  customElements.define('cart-drawer-recommendation', CartDrawerRecommendation);
}
/* ============================================================
   EDITAL CART DRAWER — FINAL INTERACTION FIXES
   Append to the END of assets/cart-drawer.js
   ============================================================ */

(() => {
  const DrawerClass = customElements.get('cart-drawer');

  /*
   * Shopify replaces the drawer HTML after every cart request. Preserve the
   * old progress value, then animate the newly-rendered bar to its new value.
   */
  if (DrawerClass && !DrawerClass.prototype.editalProgressPatched) {
    const originalRenderContents = DrawerClass.prototype.renderContents;

    DrawerClass.prototype.renderContents = function (parsedState) {
      const previousBar = this.querySelector('.edital-progress-bar');
      const previousValue = Number(previousBar?.getAttribute('aria-valuenow') || 0);

      originalRenderContents.call(this, parsedState);

      const newBar = this.querySelector('.edital-progress-bar');
      const newFill = newBar?.querySelector('.edital-progress-bar__fill');
      const maximum = Number(newBar?.getAttribute('aria-valuemax') || 5);
      const newValue = Number(newBar?.getAttribute('aria-valuenow') || 0);

      if (newFill && maximum > 0) {
        const previousPercent = Math.min(previousValue, maximum) * 100 / maximum;
        const newPercent = Math.min(newValue, maximum) * 100 / maximum;

        newFill.style.width = `${previousPercent}%`;
        newFill.getBoundingClientRect();

        requestAnimationFrame(() => {
          newFill.style.width = `${newPercent}%`;
        });
      }
    };

    DrawerClass.prototype.editalProgressPatched = true;
  }

  const RecommendationClass = customElements.get('cart-drawer-recommendation');

  /* Replace the blank loading state with an explicit "Adding…" label. */
  if (RecommendationClass && !RecommendationClass.prototype.editalLoadingPatched) {
    RecommendationClass.prototype.setLoading = function (isLoading) {
      this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.button.disabled = isLoading;

      const label = this.button.querySelector(
        '.edital-rec-row__add-label, .edital-ai-card__add-label'
      );
      const spinner = this.button.querySelector('.edital-rec-add-spinner');

      if (label) {
        if (!this.editalDefaultLabel) this.editalDefaultLabel = label.textContent.trim();
        label.textContent = isLoading ? 'Adding…' : this.editalDefaultLabel;
        label.style.visibility = 'visible';
      }

      if (spinner) spinner.classList.add('hidden');
    };

    RecommendationClass.prototype.editalLoadingPatched = true;
  }
})();
